import { map, rest } from 'lodash';
import { TOrderProduct } from 'types/Orders/products';
import { TOrderService } from 'types/Orders/services';
import { DistributionParams, OrderParams } from 'types/getParams';
import { TTask } from 'types/task';
import { TWorksheetType } from 'types/worksheets/worksheets';

import { BlobResp } from '@api/responseModels/blob/blobResponse';
import { OrderCreateResp } from '@api/responseModels/order/orderCreateResponse';
import { OrderDetailsResp } from '@api/responseModels/order/orderDetailsResponse';
import { OrderDiffsResp } from '@api/responseModels/order/orderDiffsResponse';
import { OrderDistributionResp } from '@api/responseModels/order/orderDistributionResponse';
import { OrderListResp } from '@api/responseModels/order/orderListResponse';
import { OrderPaymentLogsResp } from '@api/responseModels/order/orderPaymentLogsResponse';
import { OrderPaymentsResp } from '@api/responseModels/order/orderPaymentsResponse';
import { OrderResp } from '@api/responseModels/order/orderResponse';
import { RequestResult } from '@api/responseModels/requestResult';

import { CRMAPIBase } from '../crmApiBase';

class OrderGroup extends CRMAPIBase {
    /**
     * Получение списка заказов
     * @param crmId ID CRM
     * @returns Результат запроса
     */
    async getOrderList(params: OrderParams): Promise<RequestResult<OrderListResp>> {
        const result = await this.get<OrderListResp>('orders', { ...params });
        return result;
    }

    async getOrderDetails(
        crmId: number,
        profile_id: number
    ): Promise<RequestResult<OrderDetailsResp>> {
        const result = await this.get<OrderDetailsResp>('customer-order-params', {
            crm_id: crmId,
            customer_profile_id: profile_id,
        });
        return result;
    }

    /**
     * Получение заказа
     * @param id ID заказа
     * @param crmId ID CRM
     * @returns Результат запроса
     */
    async getOrder(id: number, crmId: number): Promise<RequestResult<OrderResp>> {
        const result = await this.get<OrderResp>(`orders/${id}`, { crm_id: crmId });
        return result;
    }

    async saveOrder(
        crm_id: number,
        products: Array<{ id: number; quantity: number; price: number }>,
        services: Array<{ id: number; quantity: number; price: number }>,
        customer_profile_id: number,
        startPlannedAt: Date,
        order_object_id,
        tasks?: Array<{ id: number; quantity: number; price: number }>,
        /** «YYYY-MM-DD HH:mm:ss»; не передано — окончание бэк не ставит */
        finishPlannedAt?: string
    ) {
        const result = await this.post<OrderCreateResp>('/orders', {
            crm_id,
            products,
            services,
            tasks,
            customer_profile_id,
            start_planned_at: startPlannedAt,
            finish_planned_at: finishPlannedAt,
            order_object_id,
        });
        return result;
    }

    async updateOrder({
        crmID,
        id,
        products,
        services,
        profileId,
        order_object_id,
        order_status_id,
        profileType,
        startPlannedAt,
        tasks,
        ...rest
    }: {
        crmID: number;
        id: number;
        products: Array<TOrderProduct>;
        services: Array<TOrderService>;
        profileId: number;
        profileType: string;
        startPlannedAt: Date;
        order_object_id: number;
        order_status_id: number;
        tasks: Array<TTask>;
    }) {
        const result = await this.put<OrderCreateResp>(`/orders/${id}`, {
            crm_id: crmID,
            products: map(products, (prod) => ({
                ...prod,
                price: Number(prod.price),
            })),
            services: services,
            customer_profile_id: profileId,
            profile_type: profileType,
            start_planned_at: startPlannedAt,
            order_object_id,
            order_status_id,
            tasks,
            ...rest,
        });
        return result;
    }

    async syncOrderFieldsInOrders(
        id: number,
        crm_id: number,
        order_fields
    ): Promise<RequestResult<OrderResp>> {
        const result = await this.post<OrderResp>(`orders/${id}/sync-fields`, {
            crm_id,
            order_fields,
        });
        return result;
    }

    async appointAnExecutor(
        id: number,
        dto: {
            crm_id: number;
            worker_positions: Array<number>;
            work_schedule_id: number;
        }
    ): Promise<RequestResult<OrderResp>> {
        const result = await this.post<OrderResp>(`orders/${id}/appointAnExecutor`, dto);
        return result;
    }

    async getDistribution(params: {
        id: number;
        crm_id: number;
    }): Promise<RequestResult<OrderDistributionResp>> {
        const result = await this.get<OrderDistributionResp>(`orders/${params.id}/distribution`, {
            crm_id: params.crm_id,
        });
        return result;
    }

    async updateDistribution(
        params: DistributionParams
    ): Promise<RequestResult<OrderDistributionResp>> {
        const { crm_id, tasks } = params;
        const result = await this.post<OrderDistributionResp>(
            `orders/${params.order_id}/distribution`,
            { crm_id, tasks }
        );
        return result;
    }

    async autoDistribution(params: {
        order_id: number;
        crm_id: number;
    }): Promise<RequestResult<OrderDistributionResp>> {
        const { crm_id } = params;
        const result = await this.post<OrderDistributionResp>(
            `orders/${params.order_id}/autoDistribution`,
            { crm_id }
        );
        return result;
    }

    async getActPdf(params: {
        order_id: number;
        crm_id: number;
        type: TWorksheetType;
    }): Promise<BlobResp> {
        let result = new RequestResult<BlobResp>();
        try {
            const response = await this._httpClient.request<BlobResp>({
                method: 'get',
                url: `orders/${params.order_id}/document`,
                params,
                responseType: 'blob',
            });
            result.statusCode = response.status;
            if (response.status >= 200 && response.status <= 300) {
                result.data = response.data;
            } else {
                result = CRMAPIBase.parseErrors<BlobResp>(response.status, response.data);
            }
        } catch (error) {
            result = CRMAPIBase.parseErrors<BlobResp>(error.response.status, error.response.data);
        }
        return result;
    }

    async deleteOrder(id: number, crm_id: number): Promise<RequestResult<OrderResp>> {
        const result = await this.delete<OrderResp>(`orders/${id}`, { crm_id });
        return result;
    }
    async deleteForceOrder(id: number, crm_id: number): Promise<RequestResult<OrderResp>> {
        const result = await this.delete<OrderResp>(`orders/${id}/forceDelete`, { crm_id });
        return result;
    }

    async restoreOrder(id: number, crm_id: number): Promise<RequestResult<OrderResp>> {
        const result = await this.post<OrderResp>(`orders/${id}/restore`, { crm_id });
        return result;
    }

    async cancelOrder(id: number, crm_id: number): Promise<RequestResult<OrderResp>> {
        const result = await this.post<OrderResp>(`orders/${id}/cancel`, { crm_id });
        return result;
    }

    async applyOrderStatusReason({
        orderId,
        crm_id,
        reason_key,
        comment,
        prev_status_id,
        ...rest
    }: {
        orderId: number;
        crm_id: number;
        reason_key: string;
        comment: string;
        prev_status_id?: number;
    }): Promise<RequestResult<OrderResp>> {
        const result = await this.post<OrderResp>(`orders/${orderId}/apply-reason`, {
            crm_id,
            reason_key,
            comment,
            prev_status_id,
            ...rest,
        });
        return result;
    }

    async getOrderPayments(crm_id, order_id): Promise<RequestResult<OrderPaymentsResp>> {
        const result = await this.get<OrderPaymentsResp>(`orders/${order_id}/payments`, { crm_id });
        return result;
    }

    async getOrderPaymentLogs(crm_id, order_id): Promise<RequestResult<OrderPaymentLogsResp>> {
        const result = await this.get<OrderPaymentLogsResp>(`orders/${order_id}/payment-logs`, {
            crm_id,
        });
        return result;
    }

    async updateOrderPayment(
        order_id,
        payload: {
            crm_id: number;
            payments: Array<any>;
        }
    ): Promise<RequestResult<null>> {
        const result = await this.post<null>(`orders/${order_id}/updatePayment`, payload);
        return result;
    }

    async getOrderDiffs(order_id, crm_id): Promise<RequestResult<OrderDiffsResp>> {
        const result = await this.get<OrderDiffsResp>(`orders/${order_id}/diff`, { crm_id });
        return result;
    }

    async applyOrderUnassignedEntitiesStatusReason({
        orderId,
        crm_id,
        reason_key,
        comment,
        prev_status_id,
        status_id,
        ...rest
    }: {
        orderId: number;
        crm_id: number;
        reason_key: string;
        comment: string;
        prev_status_id?: number;
        status_id?: string;
    }): Promise<RequestResult<null>> {
        const result = await this.post<null>(`orders/${orderId}/unassignedApplyStatus`, {
            crm_id,
            reason_key,
            comment,
            prev_status_id,
            status_id,
            ...rest,
        });
        return result;
    }

    async applyOrderGroupStatusReason({
        orderId,
        crm_id,
        reason_key,
        comment,
        prev_status_id,
        status_id,
        ...rest
    }: {
        orderId: number;
        crm_id: number;
        reason_key: string;
        comment: string;
        prev_status_id?: number;
        status_id?: number;
    }): Promise<RequestResult<null>> {
        const result = await this.post<null>(`orders/${orderId}/group-apply-reason`, {
            crm_id,
            reason_key,
            comment,
            prev_status_id,
            status_id,
            ...rest,
        });
        return result;
    }
}

export { OrderGroup };
